import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Message,
  MessageFormValues,
  MessageThreadResponse,
  SendMessageBody,
} from './types'

import { useContractUuid } from 'hooks/useContractUuid'
import { useApi } from 'utils/api'

export const useLoadMessageThread = (uuid: string) => {
  const api = useApi()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<null | 'notFound' | 'error'>(null)
  const { t } = useTranslation(['messages'])

  const [thread, setThread] = useState<{
    messages: Message[]
    title?: string
    logoUrl?: string | null
  }>({
    messages: [],
    title: t('messages:view.title'),
    logoUrl: null,
  })

  const fetchThread = useCallback(
    async (controller?: AbortController) => {
      if (!uuid) return

      setLoading(true)
      setError(null)

      try {
        const resp = await api.get<Response>(
          `/api/message/thread-link/${uuid}`,
          {
            customResponseHandling: true,
            signal: controller?.signal,
          }
        )
        const res = (await resp.json()) as MessageThreadResponse

        if (res.statusCode === 404) {
          setError('notFound')
          return
        }
        if (res.statusCode !== 200) {
          throw Error('Failed to fetch')
        }
        setError(null)
        const messages: Message[] = (res.payload?.entries ?? []).map(
          (entry) => ({
            id: entry.messageId.toString(),
            topic: entry.title ?? t('messages:view.noTitle'),
            content: entry.htmlContent ?? '',
            date: entry.timestamp,
            sender: entry.type === 'incoming_email_message' ? 'me' : 'system',
            attachments: entry.attachments.map((a) => ({
              fileName: a.fileName,
              url: a.url,
            })),
            unread:
              entry.type === 'outgoing_email_message' ? !entry.isRead : false,
          })
        )

        setThread({
          title: res.payload.title ?? t('messages:view.title'),
          logoUrl: res.payload.logoUrl ?? null,
          messages,
        })
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        setError('error')
      } finally {
        setLoading(false)
      }
    },
    [uuid, api, t]
  )

  useEffect(() => {
    // During the thread fetch, backend marks outgoing messages as read,
    // so we need to avoid making two fetches (in React dev strict mode) so
    // that we can see the unread status properly. For this we use an
    // AbortController which will abort the other request.
    const controller = new AbortController()
    fetchThread(controller)
    return () => controller.abort()
  }, [fetchThread])

  return { loading, error, thread, refetch: fetchThread }
}

export const useSendMessage = (messageThreadUuid: string | undefined) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | 'error'>(null)
  const api = useApi()
  const contractUuid = useContractUuid()

  const send = useCallback(
    async (values: MessageFormValues): Promise<true | false> => {
      setLoading(true)
      setError(null)

      const body: SendMessageBody = {
        subject: values.subject,
        htmlContent: values.message,
        fileIds: values.attachments.map((f) => f.id),
        messageThreadUuid,
        messageCategoryId:
          messageThreadUuid || !values.messageCategoryId
            ? undefined
            : Number(values.messageCategoryId),
      }

      try {
        const res = await api.post<SendMessageBody, { success: boolean }>(
          `/api/contracts/${contractUuid}/send-message`,
          body
        )

        if (res?.success) {
          setError(null)
          return true
        }
        setError('error')
        return false
      } catch (e) {
        setError('error')
        return false
      } finally {
        setLoading(false)
      }
    },
    [api, contractUuid, messageThreadUuid]
  )

  return { loading, error, send }
}
